import { getPreferredPhone, useGetAllContacts } from "@api/contactService";
import { useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import { useGetAllUsers } from "@api/userService";
import { Alert } from "@mui/joy";
import { Link } from "react-router-dom";
import { ContextualHelp } from "@components/ContextualHelp";
import { userRoles } from "@utils/selectOptions";

/*
interface HeadCell {
    disablePadding: boolean;
    id: keyof Data;
    label: string;
    numeric: boolean;
    sortable: boolean;
};
*/

const columns/*: readonly HeadCell[]*/ = [
    {
        id: 'name',
        numeric: false,
        disablePadding: true,
        sortable: false,
        label: 'Name',
        cell: (row, {LinkComponent}) => {
            // const LinkComponent = view === 'deleted' ? Box : Link;
            return (<>
                {row.contact !== null && (
                    <LinkComponent to={`/contacts/${row.contact.id}`} object={row.contact}>
                        <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>{row.first_name} {row.last_name}</Typography>
                    </LinkComponent>
                )}
                {row.contact === null && (
                    <Typography fontSize={'md'}>
                        <ContextualHelp slug="deleted_user">
                            {row.first_name || row.last_name && (<>{row.first_name} {row.last_name}</>)}
                            {!row.first_name && !row.last_name && (<>{row.email}</>)}
                        </ContextualHelp>
                    </Typography>
                )}
                {row.name !== row.company_name && (<>
                    <LinkComponent to={`/contacts/${row.id}`} object={row}>
                    <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>{row.name}</Typography>
                    </LinkComponent>
                    {row.company_name && (<Typography fontSize="sm">{row.company_name}</Typography>)}
                </>)}
                {row.name === row.company_name && (<>
                    <LinkComponent to={`/contacts/${row.id}`} object={row}>
                    <Typography fontWeight={'bold'}>{row.company_name}</Typography>
                    </LinkComponent>
                </>)}
            </>)
        }
      },
      {
        id: 'email',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Email',
        cell: (row) => row.email
      },
      {
        id: 'role',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Role',
        cell: (row) => <>{row.roles.map(role => {
            const findRole = userRoles.find(r => r.value === role.id);
            return findRole?.label ?? role.name;
        }).join(', ')}</>,
      },
];

const listCell = (row, {LinkComponent}) => {
    return (<>
    {row.name !== row.company_name && (
        <Box sx={{mb: 1}}>
            <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>
                <LinkComponent to={`/contacts/${row.id}`} object={row}>
                    {row.name}
                </LinkComponent>
            </Typography>
            {row.company_name && (<Typography fontSize="sm">{row.company_name}</Typography>)}
        </Box>
    )}
    {row.name === row.company_name && (
        <Box sx={{mb: 1}}>
            <Typography>
                <LinkComponent to={`/contacts/${row.id}`} object={row}>
                    {row.company_name}
                </LinkComponent>
            </Typography>
        </Box>
        )}
    </>);
};

const listActions = (row, {view, LinkComponent, ViewAction}) => {
    if(view === 'deleted') {
        return null;
    }

    return (<>
        <Tooltip title="Email">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
                disabled={!row.email}
                component={'a'}
                href={`mailto:${row.email}`}
                >
                <EmailRoundedIcon />
            </IconButton>
        </Tooltip>
        <Tooltip title="Call">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
                disabled={!getPreferredPhone(row)}
                component={'a'}
                href={`tel:${getPreferredPhone(row)}`}
                >
                <LocalPhoneRoundedIcon />
            </IconButton>
        </Tooltip>
    </>);
};

export default function Users() {
    return (
        <ObjectIndex
            query={useGetAllUsers}
            type={'user'}
            permissionType={'noop'}
            title={'Users'}
            color={'default'}
            columns={columns}
            listCell={listCell}
            listActions={listActions}
        >
            <Alert
                color="neutral"
                variant="outlined"
                sx={{mb: 2, fontSize: 'md', gap: 0}}
            >
                <Typography>
                Users and contacts have been combined. To edit user access, please make changes in the <Link to="/contacts">contacts</Link> section.
                </Typography>
            </Alert>
        </ObjectIndex>
    )
}
